import {
  IAccountCredit,
  IInvoice,
  ILabJob,
  InvoiceStatus,
  IPatient,
  IPractice,
  isDepositLineItem,
  IStaffer,
  isTreatmentLineItem,
  ITransaction,
  IUsedAccountCredit,
  LabJobIncomeTimestampField,
  TransactionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  INamedDocument,
  isSameRef,
  Timestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact, flatten, uniqWith } from 'lodash';
import {
  IPractitionerIncomeSummary,
  PractitionerIncomeSummary,
} from './practitioner-income-summary';
import { createRecordSummary } from './practitioner-income-summary-creator';

export interface IInvoiceReportRequest {
  startDate: Timestamp;
  endDate: Timestamp;
  practiceRef: DocumentReference<IPractice>;
  status?: InvoiceStatus;
  practitionerRef?: DocumentReference<IStaffer>;
  labJobTimestampField?: LabJobIncomeTimestampField;
}

export interface ITransactionReportRequest {
  startDate: Timestamp;
  endDate: Timestamp;
  practiceRef: DocumentReference<IPractice>;
  status?: TransactionStatus;
  practitionerRef?: DocumentReference<IStaffer>;
  labJobTimestampField?: LabJobIncomeTimestampField;
}

export interface IInvoiceReportRecord {
  invoice: WithRef<IInvoice>;
  patient: WithRef<IPatient>;
  transactions: WithRef<Omit<ITransaction, 'extendedData'>>[];
}

export interface IAccountCreditReportRecord {
  accountCredit: WithRef<IAccountCredit> & { remaining: number };
  invoice?: WithRef<IInvoice>;
  patient: WithRef<IPatient>;
  transactions: WithRef<ITransaction>[];
}

export interface ITransactionReportRecord {
  invoice: WithRef<IInvoice>;
  patient: INamedDocument<IPatient>;
  transaction: WithRef<ITransaction>;
  accountCreditTransactions: IAccountCreditTransactionsRecord[];
}

export interface IAccountCreditTransactionsRecord {
  creditUsed: IUsedAccountCredit;
  depositPayments: IDepostPaymentBreakdown[];
}

export interface IDepostPaymentBreakdown {
  transaction: WithRef<ITransaction>;
  totalTransactionAmount: number;
  transactionRatio: number;
  transactionAmount: number;
}

export interface IPractitionerReportRecord {
  patient: WithRef<IPatient>;
  practitioner: INamedDocument<IStaffer>;
  invoice: WithRef<IInvoice>;
  summary: IPractitionerIncomeSummary;
}

export interface IPractitionerLabFeeReportRecord {
  labJob: WithRef<ILabJob>;
  practitioner: INamedDocument<IStaffer>;
  summary: Pick<IPractitionerIncomeSummary, 'labFeesAmount'>;
}

export function toPractitionerIncomeRecords(
  records: IInvoiceReportRecord[]
): IPractitionerReportRecord[] {
  return flatten(
    records.map((record) => groupedPractitionerIncomeRecords(record))
  );
}

function groupedPractitionerIncomeRecords(
  record: IInvoiceReportRecord
): IPractitionerReportRecord[] {
  const treatmentPractitioners = record.invoice.items
    .filter(isTreatmentLineItem)
    .map((treatment) => treatment.treatmentRef.attributedTo);
  const depositPractitioners = compact(
    record.invoice.items
      .filter(isDepositLineItem)
      .map((deposit) => deposit.attributedTo)
  );
  return uniqWith(
    [...treatmentPractitioners, ...depositPractitioners],
    isSameRef
  ).map((practitioner) => ({
    invoice: record.invoice,
    patient: record.patient,
    practitioner,
    summary: createRecordSummary(
      record.invoice,
      record.transactions,
      practitioner.ref
    ),
  }));
}

export interface IPractitionerGrouping {
  practitioner: INamedDocument<IStaffer>;
  records: IPractitionerReportRecord[];
  labFeeRecords: IPractitionerLabFeeReportRecord[];
  total: IPractitionerIncomeSummary;
}

export function practitionerSummary(
  records: IPractitionerReportRecord[],
  labFeeRecords: IPractitionerLabFeeReportRecord[] = []
): IPractitionerGrouping[] {
  const practitioners = uniqWith(
    [
      ...records.map((record) => record.practitioner),
      ...labFeeRecords.map((record) => record.practitioner),
    ],
    isSameRef
  );

  return practitioners.map((practitioner) => {
    const practitionerRecords = records.filter((record) =>
      isSameRef(record.practitioner, practitioner)
    );
    const practitionerLabFeeRecords = labFeeRecords.filter((record) =>
      isSameRef(record.practitioner, practitioner)
    );

    return {
      practitioner,
      records: practitionerRecords,
      labFeeRecords: practitionerLabFeeRecords,
      total: PractitionerIncomeSummary.reduce([
        ...practitionerRecords.map((item) => item.summary),
        ...practitionerLabFeeRecords.map((item) =>
          PractitionerIncomeSummary.init(item.summary)
        ),
      ]),
    };
  });
}

export function toPractitionerLabFeeRecords(
  labJobs: WithRef<ILabJob>[]
): IPractitionerLabFeeReportRecord[] {
  return compact(
    labJobs.map((labJob) => {
      if (!labJob.practitioner) {
        return;
      }

      return {
        labJob,
        practitioner: labJob.practitioner,
        summary: { labFeesAmount: labJob.cost },
      };
    })
  );
}
